import React, { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useBranding, assetUrl } from "../lib/useBranding";
import HamburgerIcon from "./HamburgerIcon";
import BookNowButton from "./BookNowButton";

export const Logo = ({ onClick }) => {
  const branding = useBranding();
  // Top nav / dashboard sidebar / footer / mobile drawer: show the PRIMARY logo
  // uploaded by the admin in Settings → Branding → "Primary logo". Falls back
  // through the light variant, then the packaged asset so nothing is ever blank.
  const logoUrl = assetUrl(branding.logo_url)
    || assetUrl(branding.logo_url_light)
    || "/icon-512.png";
  return (
    <Link to="/" onClick={onClick} className="flex items-center select-none" data-testid="nav-logo" aria-label="Talentsio home">
      <img src={logoUrl} alt="Talentsio" data-testid="nav-logo-img"
        className="h-9 w-auto max-w-[180px] object-contain" />
    </Link>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t("nav.how"), to: "/how-it-works" },
    { label: t("nav.spotlight"), to: "/spotlight" },
    { label: t("nav.talents"), to: "/for-talents" },
    { label: t("nav.pricing"), to: "/pricing" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#0D0D0D]" : "bg-transparent"}`}>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 translate-y-[3px]">
            {links.map((l) => (
              <Link key={l.label} to={l.to} className="text-[15px] font-bold text-neutral-300 hover:text-white transition-colors">{l.label}</Link>
            ))}
          </nav>
        </div>
        <div className="hidden md:flex items-center gap-6 translate-y-[3px]">
          <LanguageSwitcher />
          {user ? (
            <button onClick={() => navigate("/dashboard")} className="text-[15px] text-neutral-300 hover:text-white transition-colors">Dashboard</button>
          ) : (
            <Link to="/signin" className="text-[15px] text-neutral-300 hover:text-white transition-colors">{t("nav.signin")}</Link>
          )}
          <BookNowButton />
        </div>
        <div className="md:hidden flex items-center gap-3">
          <BookNowButton testId="nav-book-mobile" />
          <button className="text-white" onClick={() => setOpen(true)} aria-label="Menu" data-testid="nav-hamburger">
            <HamburgerIcon />
          </button>
        </div>
      </div>
      <div className={`md:hidden fixed inset-0 z-50 ${open ? "pointer-events-auto" : "pointer-events-none"}`} aria-hidden={!open}>
        <div className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} data-testid="nav-overlay" />
        <aside data-testid="mobile-drawer" className={`absolute top-0 right-0 h-full w-[300px] bg-[#0D0D0D] border-l border-[#1e1e1e] flex flex-col p-6 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between mb-8">
            <Logo onClick={() => setOpen(false)} />
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="drawer-close" className="text-neutral-400 hover:text-white transition-colors"><X size={24} /></button>
          </div>
          <nav className="flex flex-col gap-5 flex-1">
            {links.map((l) => (<Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-[16px] text-neutral-300 hover:text-white transition-colors">{l.label}</Link>))}
            <Link to="/faq" onClick={() => setOpen(false)} data-testid="drawer-faq" className="text-[16px] text-neutral-300 hover:text-white transition-colors">FAQ</Link>
            <div className="h-px bg-[#1e1e1e] my-1" />
            <div className="flex items-center justify-between">
              <LanguageSwitcher />
              {user ? <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="text-neutral-300 hover:text-white transition-colors">Dashboard</button> : <Link to="/signin" onClick={() => setOpen(false)} className="text-neutral-300 hover:text-white transition-colors">{t("nav.signin")}</Link>}
            </div>
          </nav>
          <div className="h-px bg-[#1e1e1e] mb-4" />
          <Link to="/boka" onClick={() => setOpen(false)} data-testid="drawer-book-btn" className="bg-[#1A8A4A] hover:bg-[#20A458] text-white font-medium px-5 py-3 rounded-full text-center flex items-center justify-center gap-2 transition-colors">{t("nav.book")} <ArrowRight size={16} /></Link>
        </aside>
      </div>
    </header>
  );
};

export default Navbar;
